/**
 * Preview quality tiers.
 *
 * The editor plays a proxy, never the master. A 4K screen recording decodes far
 * too slowly for a browser to composite in real time, and when the composition
 * falls behind, Remotion drags the audio elements back into sync — which is
 * heard as stuttering narration rather than as a slow preview.
 *
 * Tiers exist because the right trade differs by machine and by task: judging
 * narration wants smooth playback, checking a zoom's framing wants pixels.
 * None of this reaches the render, which always uses the master.
 */
export const previewQualities = ["performance", "balanced", "high"] as const;
export type PreviewQuality = (typeof previewQualities)[number];

export interface PreviewTier {
  /** Proxy width in pixels; height follows the source's aspect. */
  width: number;
  /**
   * Frames per second for both the proxy and the composition while previewing.
   * Capped by the timeline's own rate — a preview never invents frames.
   */
  fps: number;
  label: string;
  hint: string;
}

export const previewTiers: Record<PreviewQuality, PreviewTier> = {
  performance: { width: 960, fps: 30, label: "Performance", hint: "Smoothest playback. Best for judging narration." },
  balanced: { width: 1280, fps: 30, label: "Balanced", hint: "The default. Smooth, and sharp enough to frame a zoom." },
  high: { width: 1920, fps: 60, label: "High", hint: "Closest to the render. Needs a fast machine." },
};

export const defaultPreviewQuality: PreviewQuality = "balanced";

/** Which tier a proxy of this width belongs to, for matching stored assets. */
export function tierForWidth(width: number): PreviewQuality | null {
  const match = previewQualities.find((quality) => previewTiers[quality].width === width);
  return match ?? null;
}
